import { produce } from "immer";
import { useMemo, useReducer } from "react";
import type { QuoteDetailsMO } from "~/OrdersQuotes/common/components/v2/models";
import {
  type QuoteDetailsSelectionState,
  quoteDetailsSelectionStateReducer,
} from "~/OrdersQuotes/common/components/v2/QuoteTable/useQuoteDetailsSelectionState";

function init(quoteDetails: QuoteDetailsMO, preselect: boolean) {
  return {
    type: "INIT",
    payload: { soNumber: quoteDetails.soNumber, quoteDetails, preselect },
  } as const;
}

function toggleLine(soNumber: string, lineNo: string) {
  return {
    type: "TOGGLE_LINE",
    payload: { soNumber, lineNo },
  } as const;
}

function toggleAllLines(soNumber: string) {
  return {
    type: "TOGGLE_ALL_LINES",
    payload: { soNumber },
  } as const;
}

type SelectionStateActions =
  | ReturnType<typeof toggleLine>
  | ReturnType<typeof toggleAllLines>
  | ReturnType<typeof init>;

export interface QuoteListSelectionState {
  selectionPerDocument: Record<string, QuoteDetailsSelectionState>;
}

const INITIAL_STATE: QuoteListSelectionState = {
  selectionPerDocument: {},
};

export function quoteListSelectionStateReducer(state: QuoteListSelectionState, action: SelectionStateActions) {
  return produce(state, (draft) => {
    const soNumber = action.payload.soNumber;
    const documentState = draft.selectionPerDocument[soNumber] ?? quoteDetailsSelectionStateReducer.initialState;

    let nestedAction = undefined;
    switch (action.type) {
      case "INIT": {
        nestedAction = quoteDetailsSelectionStateReducer.actions.init(
          action.payload.quoteDetails,
          action.payload.preselect
        );
        break;
      }
      case "TOGGLE_LINE": {
        nestedAction = quoteDetailsSelectionStateReducer.actions.toggleLine(action.payload.lineNo);
        break;
      }
      case "TOGGLE_ALL_LINES": {
        nestedAction = quoteDetailsSelectionStateReducer.actions.toggleAllLines();
        break;
      }
    }
    if (nestedAction) {
      draft.selectionPerDocument[soNumber] = quoteDetailsSelectionStateReducer(documentState, nestedAction);
    }
  });
}

export function useQuoteListSelectionState() {
  const [state, dispatch] = useReducer(quoteListSelectionStateReducer, INITIAL_STATE);

  const actions = useMemo(
    () => ({
      init: (quoteDetails: QuoteDetailsMO, preselect: boolean) => dispatch(init(quoteDetails, preselect)),
      toggleLine: (soNumber: string, lineNo: string) => dispatch(toggleLine(soNumber, lineNo)),
      toggleAllLines: (soNumber: string) => dispatch(toggleAllLines(soNumber)),
    }),
    [dispatch]
  );

  return {
    ...state,
    ...actions,
  };
}
